/**
 * Gestionnaire centralisé pour toutes les modales - VERSION CORRIGÉE
 * Permet d'enregistrer, ouvrir, fermer et gérer les modales de manière unifiée
 */

import { BaseModal } from './BaseModal';

export interface ModalRegistration {
  id: string;
  instance: BaseModal;
  type: 'service' | 'prompt' | 'cookie' | 'formation' | 'custom';
  isInitialized: boolean;
}

export class ModalRegistry {
  private static instance: ModalRegistry;
  private modals: Map<string, ModalRegistration> = new Map();
  private currentOpenModal: string | null = null;
  private globalKeyboardHandlers: Array<(e: KeyboardEvent) => void> = [];

  private constructor() {
    this.setupGlobalKeyboardHandlers();
  }

  /**
   * Singleton pattern
   */
  public static getInstance(): ModalRegistry {
    if (!ModalRegistry.instance) {
      ModalRegistry.instance = new ModalRegistry();
    }
    return ModalRegistry.instance;
  }

  /**
   * Configuration des gestionnaires de clavier globaux
   */
  private setupGlobalKeyboardHandlers(): void {
    const globalHandler = (e: KeyboardEvent) => {
      // Gérer la navigation entre modales avec Tab
      if (e.key === 'Tab' && this.currentOpenModal) {
        // Empêcher le focus de sortir de la modale
        this.trapFocus(e);
      }
    };

    document.addEventListener('keydown', globalHandler);
    this.globalKeyboardHandlers.push(globalHandler);
  }

  /**
   * Piège le focus dans la modale ouverte
   */
  private trapFocus(e: KeyboardEvent): void {
    const currentModal = this.getCurrentModal();
    if (!currentModal || !currentModal.modalElement) return;

    const focusableElements = currentModal.modalElement.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );

    const firstElement = focusableElements[0] as HTMLElement;
    const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement;

    if (e.shiftKey) {
      if (document.activeElement === firstElement) {
        lastElement?.focus();
        e.preventDefault();
      }
    } else {
      if (document.activeElement === lastElement) {
        firstElement?.focus();
        e.preventDefault();
      }
    }
  }

  /**
   * Enregistre une nouvelle modale
   */
  public register(modal: BaseModal, type: ModalRegistration['type']): void {
    if (!modal.config.id) {
      console.error('Modal sans ID, impossible de l\'enregistrer');
      return;
    }

    const registration: ModalRegistration = {
      id: modal.config.id,
      instance: modal,
      type,
      isInitialized: false
    };

    this.modals.set(modal.config.id, registration);
    console.log(`🎭 Modal '${modal.config.id}' enregistrée avec le type '${type}'`);
  }

  /**
   * Initialise une modale (crée les éléments DOM)
   */
  public init(modalId: string): boolean {
    const registration = this.modals.get(modalId);
    if (!registration) {
      console.warn(`❌ Modal '${modalId}' non trouvée dans le registre`);
      return false;
    }

    if (!registration.isInitialized) {
      try {
        registration.instance.init();
        registration.isInitialized = true;
        console.log(`✅ Modal '${modalId}' initialisée`);
        return true;
      } catch (error) {
        console.error(`❌ Erreur lors de l'initialisation de la modal '${modalId}':`, error);
        return false;
      }
    }

    return true;
  }

  /**
   * Ouvre une modale spécifique
   */
  public async open(modalId: string, data?: any): Promise<boolean> {
    // Fermer la modale actuellement ouverte
    if (this.currentOpenModal && this.currentOpenModal !== modalId) {
      this.close(this.currentOpenModal);
    }

    const registration = this.modals.get(modalId);
    if (!registration) {
      console.warn(`❌ Modal '${modalId}' non trouvée`);
      return false;
    }

    // Initialiser si nécessaire
    if (!registration.isInitialized) {
      if (!this.init(modalId)) {
        return false;
      }
    }

    // Passer les données à la modale si nécessaire
    if (data && 'setData' in registration.instance) {
      try {
        const setDataResult = (registration.instance as any).setData(data);
        // Si setData retourne une Promise, l'attendre
        if (setDataResult && typeof setDataResult.then === 'function') {
          await setDataResult;
        }
        
        // Forcer le re-rendu après setData
        registration.instance.updateContent();
        
      } catch (error) {
        console.error(`❌ Erreur lors du passage des données à la modal '${modalId}':`, error);
        return false;
      }
    }

    try {
      registration.instance.open();
      this.currentOpenModal = modalId;
      return true;
    } catch (error) {
      console.error(`❌ Erreur lors de l'ouverture de la modal '${modalId}':`, error);
      return false;
    }
  }

  /**
   * Ferme une modale spécifique
   */
  public close(modalId: string): boolean {
    const registration = this.modals.get(modalId);
    if (!registration) {
      console.warn(`❌ Modal '${modalId}' non trouvée`);
      return false;
    }

    try {
      registration.instance.close();
      
      if (this.currentOpenModal === modalId) {
        this.currentOpenModal = null;
      }

      console.log(`🎭 Modal '${modalId}' fermée`);
      return true;
    } catch (error) {
      console.error(`❌ Erreur lors de la fermeture de la modal '${modalId}':`, error);
      return false;
    }
  }

  /**
   * Ferme toutes les modales ouvertes
   */
  public closeAll(): void {
    this.modals.forEach((registration) => {
      if (registration.instance.isModalOpen) {
        registration.instance.close();
      }
    });
    this.currentOpenModal = null;
    console.log('🎭 Toutes les modales fermées');
  }

  /**
   * Supprime une modale du registre
   */
  public unregister(modalId: string): boolean {
    const registration = this.modals.get(modalId);
    if (!registration) return false;

    try {
      // Fermer et détruire la modale
      if (registration.instance.isModalOpen) {
        registration.instance.close();
      }
      registration.instance.destroy();

      // Supprimer du registre
      this.modals.delete(modalId);
      
      if (this.currentOpenModal === modalId) {
        this.currentOpenModal = null;
      }

      console.log(`🗑️ Modal '${modalId}' supprimée du registre`);
      return true;
    } catch (error) {
      console.error(`❌ Erreur lors de la suppression de la modal '${modalId}':`, error);
      return false;
    }
  }

  /**
   * Récupère la modale actuellement ouverte
   */
  public getCurrentModal(): BaseModal | null {
    if (!this.currentOpenModal) return null;
    
    const registration = this.modals.get(this.currentOpenModal);
    return registration ? registration.instance : null;
  }

  /**
   * Récupère une modale par son ID
   */
  public getModal(modalId: string): BaseModal | null {
    const registration = this.modals.get(modalId);
    return registration ? registration.instance : null;
  }

  /**
   * Liste toutes les modales enregistrées
   */
  public listModals(): ModalRegistration[] {
    return Array.from(this.modals.values());
  }

  /**
   * Vérifie si une modale est ouverte
   */
  public isAnyModalOpen(): boolean {
    return this.currentOpenModal !== null;
  }

  /**
   * Vérifie si une modale spécifique est ouverte
   */
  public isModalOpen(modalId: string): boolean {
    const registration = this.modals.get(modalId);
    return registration ? registration.instance.isModalOpen : false;
  }

  /**
   * Met à jour le contenu d'une modale
   */
  public async updateModal(modalId: string, data?: any): Promise<boolean> {
    const registration = this.modals.get(modalId);
    if (!registration) return false;

    try {
      if (data && 'setData' in registration.instance) {
        const setDataResult = (registration.instance as any).setData(data);
        // Si setData retourne une Promise, l'attendre
        if (setDataResult && typeof setDataResult.then === 'function') {
          await setDataResult;
        }
      }

      registration.instance.updateContent();
      return true;
    } catch (error) {
      console.error(`❌ Erreur lors de la mise à jour de la modal '${modalId}':`, error);
      return false;
    }
  }

  /**
   * Nettoie toutes les ressources
   */
  public destroy(): void {
    // Supprimer les gestionnaires globaux
    this.globalKeyboardHandlers.forEach(handler => {
      document.removeEventListener('keydown', handler);
    });
    this.globalKeyboardHandlers = [];

    // Détruire toutes les modales
    this.modals.forEach((registration) => {
      try {
        registration.instance.destroy();
      } catch (error) {
        console.error(`Erreur lors de la destruction de la modal ${registration.id}:`, error);
      }
    });

    this.modals.clear();
    this.currentOpenModal = null;
    
    console.log('🧹 ModalRegistry nettoyé');
  }
}

// Export de l'instance singleton pour facilité d'utilisation
export const modalRegistry = ModalRegistry.getInstance();
